import { Dictionary, keyBy, uniq } from 'lodash';
import moment from 'moment';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Role, Team, User, UserType, Company } from 'api/models';
import { ResourceManagmentApi } from 'api/resource-management';
import {
  PlanningLevelConfig,
  Allocation,
  FetchableResourceType,
  ListOfResourcesToBeFetched,
  ResourceAllocations,
  ResourceManagementConfig,
  ResourcesToBeFetched,
  ResourceType,
} from 'api/resource-management/types';
import { Switch } from 'components/atoms/switch/switch';
import { PlaceholderCreateOrEditModal } from 'components/organisms/placeholders/create-edit-modal';
import {
  FindMemberModal,
  useFindMember,
} from 'components/organisms/resource-management/add-to-project';
import { AutoAllocateSourceTasksDrawer } from 'components/organisms/resource-management/auto-allocation-source-tasks';
import { AvailabilityFilter } from 'components/organisms/resource-management/availability-filter';
import {
  ResourceManagementEvents,
  TimeOffEvent,
} from 'components/organisms/resource-management/event-types';
import {
  ExportAsCSVModal,
  useControlExport,
} from 'components/organisms/resource-management/export';
import {
  useAvailabilityFilter,
  usePeopleViewFilter,
} from 'components/organisms/resource-management/filters';
import { ResourceManagementLegendsForPeopleTab as Legends } from 'components/organisms/resource-management/legend';
import { ManageAllocationDrawer } from 'components/organisms/resource-management/manage-allocation';
import {
  CommonManagerProps,
  ManagerPropsWithLoadedRange,
  useAggregationManager,
  useAllocationLoader,
  useAllocationManager,
  useAllocationsMoreActionsManager,
  useAllocationWSManager,
  useResourceManager,
  useResourcesLoader,
  useViewPresetManager,
} from 'components/organisms/resource-management/managers';
import { useAllocationStateManager } from 'components/organisms/resource-management/managers/allocation-state-manager';
import { SchedulerResourceType } from 'components/organisms/resource-management/resource-types';
import { getCalendars } from 'components/organisms/resource-management/scheduler-config/calendars';
import { useColumnsConfigForGroupByUser } from 'components/organisms/resource-management/scheduler-config/columns/columns-for-group-by-users';
import { useEventsTooltip } from 'components/organisms/resource-management/scheduler-config/event-tooltips';
import {
  useConvertAllocations,
  useDeleteAllocations,
  UserRemovedFromProjectParams,
  useShowSourceTasks,
  useTeamRemovedFromProject,
  useToStartOfProject,
  useUserAssignedToProject,
  useUserRemovedFromProject,
} from 'components/organisms/resource-management/scheduler-config/listeners';
import {
  AllocationEventModel,
  GroupByUserResourceModel,
} from 'components/organisms/resource-management/scheduler-config/models';
import { ViewPresetId } from 'components/organisms/resource-management/scheduler-config/view-presets/constants';
import {
  getUserSelectablePresetId,
  getZoomPresets,
  UserSelectablePresetIds,
  UserSelectablePresetIdsForHoursPerWeek,
  getZoomPresetsForHoursPerWeek,
} from 'components/organisms/resource-management/scheduler-config/view-presets/view-presets';
import { Scheduler } from 'components/organisms/resource-management/scheduler/scheduler';
import { SecondaryToolbar } from 'components/organisms/resource-management/secondary-toolbar';
import { ResourceManagementSocketConnector } from 'components/organisms/resource-management/socket-connector';
import {
  GroupBy,
  ProjectsWithMembers,
  TeamsGroupedByCompany,
  UsersGroupedByCompany,
  UserWithProjects,
} from 'components/organisms/resource-management/types';
import { useLocalStorageState } from 'components/organisms/resource-management/use-localstorage-state';
import { usePageParam } from 'components/organisms/resource-management/use-page-params';
import {
  getProjectStatuses,
  getTeamsWithProjectGroupedByRole,
  getTeamsWithProject,
  filterUnresolvedVendorResponsibleTeams,
} from 'components/organisms/resource-management/utils/associated-data';
import { concat } from 'components/organisms/resource-management/utils/common';
import { getCorrectedDateRangeBasedOnView } from 'components/organisms/resource-management/utils/dates';
import { convertAllocationsByUsersOrTeamsToEvents } from 'components/organisms/resource-management/utils/events-conversion/allocations-by-users-or-teams';
import { asAllocationPayload } from 'components/organisms/resource-management/utils/events-conversion/common';
import { convertTimeOffAsSummaryEvents } from 'components/organisms/resource-management/utils/events-conversion/resource-summary/timeoff-summary';
import { resourceIdForTeamAsLeaf } from 'components/organisms/resource-management/utils/id';
import {
  convertUsersToResourceTree,
  teamsWithProjectGroupedByRoleAsResource,
  convertUserGroupedByCompanyToResourceTree,
} from 'components/organisms/resource-management/utils/resource-tree';
import {
  getMaximumEndDateFromListOfRanges,
  getMinimumStartDateFromListOfRanges,
} from 'components/organisms/resource-management/utils/resources-summary/common';
import {
  getNonZeroSummaryEvents,
  getResourceSummaries,
} from 'components/organisms/resource-management/utils/resources-summary/resource-summary';
import { useTeamCellActions } from 'components/organisms/resource-management/utils/use-team-cell-actions';
import {
  DEFAULT_VIEW_ID,
  useGroupByUsersDefaultViews,
  Views as ViewsComponent,
} from 'components/organisms/resource-management/views';
import { FilterableField } from 'components/organisms/tasks/fields';
import { WorkingDaysConfiguration } from 'components/organisms/user-capacity';
import { useFieldsAsGroupedFields } from 'components/pages/all-tasks/use-fields-as-grouped-fields';
import { GroupFieldsMeta } from 'components/pages/filter-transformer';
import { FilterGroupType } from 'components/pages/types';
import { useIsCurrentUserCollaborator } from 'hooks/permissions';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { queueMacrotask } from 'utils/macrotask';
import { ResourceManagementPage } from '../page';

interface Props {
  roles: Role[];
  users: User[];
  teams: Team[];
  projects: ProjectsWithMembers[];
  roleLookup: Dictionary<Role>;
  userLookup: Dictionary<User>;
  teamLookup: Dictionary<Team>;
  projectLookup: Dictionary<ProjectsWithMembers>;
  teamMembership: Record<string, Team[]>;
  usersWithProjects: Dictionary<UserWithProjects>;
  workingDaysConfig: WorkingDaysConfiguration;
  userFields: FilterableField[];
  isUserFieldsLoading: boolean;
  projectFields: FilterableField[];
  isProjectFieldsLoading: boolean;
  statusFieldId?: number;
  resourceManagementConfig?: ResourceManagementConfig;
  userIdsGroupedByCompany: UsersGroupedByCompany;
  partnerCompanyLookup: Dictionary<Company>;
  teamIdsGroupedByCompany: TeamsGroupedByCompany;
}
const SwitchWrapper = styled.div`
  display: flex;
  gap: var(--spacing-02);
  align-items: center;
  margin-left: var(--spacing-05);
`;
const SchedulerContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

export const GroupByUsers: FC<Props> = ({
  users,
  teams,
  projects,
  workingDaysConfig,
  roles,
  roleLookup,
  teamLookup,
  userLookup,
  projectLookup,
  teamMembership,
  usersWithProjects,
  userFields,
  isUserFieldsLoading,
  projectFields,
  isProjectFieldsLoading,
  statusFieldId,
  resourceManagementConfig,
  partnerCompanyLookup,
  userIdsGroupedByCompany,
  teamIdsGroupedByCompany,
}) => {
  const browserTitle = `${useTranslation(
    'resource_management.tabs.users',
  )} - ${useTranslation('resource_management.title')}`;

  const schedulerRef = useRef<any>();
  const [searchText, setSearchText] = useState('');
  const [canInitializeWS, setInitializeWS] = useState(false);
  const [hideUnavailableAreas, setHideUnavailableAreas] = useState(true);

  const currentUser = useUser();
  const isCollaborator = useIsCurrentUserCollaborator();

  const user_fields_lookup_by_name = useMemo(
    (): Record<string, FilterableField> =>
      keyBy(isUserFieldsLoading ? [] : userFields, 'fieldName'),
    [userFields, isUserFieldsLoading],
  );

  const projects_fields_lookup_by_name = useMemo(
    (): Record<string, FilterableField> =>
      keyBy(isProjectFieldsLoading ? [] : projectFields, 'fieldName'),
    [isProjectFieldsLoading, projectFields],
  );

  const { groupedFields } = useFieldsAsGroupedFields({
    userFields,
    projectFields,
  });

  const groupedLookupsByName: GroupFieldsMeta = useMemo(() => {
    return [
      {
        id: FilterGroupType.User,
        fields: user_fields_lookup_by_name,
      },
      {
        id: FilterGroupType.Project,
        fields: projects_fields_lookup_by_name,
      },
    ];
  }, [projects_fields_lookup_by_name, user_fields_lookup_by_name]);

  const defaultViews = useGroupByUsersDefaultViews(
    groupedLookupsByName,
    isUserFieldsLoading || isProjectFieldsLoading,
  );

  const isHoursPerWeek = useMemo(
    () =>
      resourceManagementConfig?.planningLevelConfig ===
      PlanningLevelConfig.HoursPerWeek,
    [resourceManagementConfig?.planningLevelConfig],
  );

  const {
    pageParams,
    setPageParams,
    replacePageParams,
    toUrlFormat,
    fromUrlFormat,
  } = usePageParam(
    groupedLookupsByName,
    FilterGroupType.User,
    isUserFieldsLoading || isProjectFieldsLoading,
    isHoursPerWeek,
  );

  const isStateRestored = useLocalStorageState({
    loading: isUserFieldsLoading || isProjectFieldsLoading,
    pageParams,
    replacePageParams,
    toUrlFormat,
    fromUrlFormat,
    key: 'resource_management.users_view.page_param',
  });

  const onFilterReset = useCallback(() => {
    setSearchText('');
    const defaultView = defaultViews.find(
      ({ viewId }) => viewId === DEFAULT_VIEW_ID,
    );

    setPageParams({
      ...pageParams,
      ...defaultView?.viewDefinition,
      availabilityFilter: undefined,
      viewId: defaultView?.viewId,
    });
  }, [defaultViews, pageParams, setPageParams]);

  const zoomPresets = useMemo(
    () =>
      isHoursPerWeek
        ? getZoomPresetsForHoursPerWeek(workingDaysConfig)
        : getZoomPresets(workingDaysConfig),
    [workingDaysConfig, isHoursPerWeek],
  );

  const projectStatusLookup = useMemo(
    () => getProjectStatuses(projects, statusFieldId),
    [projects, statusFieldId],
  );
  const { getAllocatedMinutesFromAggregation } = useAggregationManager();

  const onAllocationLoaded = useCallback(
    (resourceAllocations: ResourceAllocations) =>
      new Promise<void>((resolve) => {
        if (isCollaborator) {
          schedulerRef.current.instance.resources.forEach(({ id }) => {
            schedulerRef.current?.instance.expand(id);
          });
        }

        const events: ResourceManagementEvents[] = [];
        const newAllocationEvents: ResourceManagementEvents[] =
          convertAllocationsByUsersOrTeamsToEvents(
            resourceAllocations,
            {
              excludeNonWorkingDays: true,
              workingDaysConfig,
            },
            resourceManagementConfig?.softAllocations.enabled,
          );

        events.push(...newAllocationEvents);

        const timeoffAsSummaryEvents: TimeOffEvent[] =
          convertTimeOffAsSummaryEvents(userLookup, schedulerRef);

        events.push(...timeoffAsSummaryEvents);

        const { userSummaries, teamSummaries, companySummaries } =
          getResourceSummaries(
            userLookup,
            teamMembership,
            resourceAllocations,
            pageParams.viewPreset,
            resourceManagementConfig?.softAllocations.enabled,
          );

        const filteredTeamEvents = getNonZeroSummaryEvents(
          Object.values(teamSummaries).flat(),
        );
        const nonZeroCompanyEvents = getNonZeroSummaryEvents(
          Object.values(companySummaries).flat(),
        );

        events.push(
          ...Object.values(userSummaries).flat(),
          ...filteredTeamEvents,
          ...nonZeroCompanyEvents,
        );

        const nonDuplicateEvents = [
          ViewPresetId.Day,
          ViewPresetId.DayZoomed,
        ].includes(pageParams.viewPreset)
          ? events
          : events.filter(
              (event) =>
                !schedulerRef.current.instance.eventStore.isAvailable(event.id),
            );

        if (nonDuplicateEvents.length > 0) {
          schedulerRef.current.instance.suspendRefresh();
          schedulerRef.current.instance.eventStore.add(nonDuplicateEvents);
          schedulerRef.current.instance.resumeRefresh(true);
        }
        const setTotalAllocationsForRoleResource = (roleResource) => {
          if (roleResource == null) {
            return;
          }
          roleResource.children.forEach((teamResource) => {
            if (teamResource != null) {
              teamResource.set(
                'totalAllocation',
                getAllocatedMinutesFromAggregation(teamResource.projectId, {
                  type: ResourceType.Team,
                  teamId: teamResource.teamId,
                }).totalAllocatedMinutes,
              );
            }
          });
        };

        const setTotalAllocationsForUserResource = (userResource) => {
          userResource.children.forEach((childResource) => {
            if (
              childResource == null ||
              childResource.type !== SchedulerResourceType.Project
            ) {
              return;
            }

            childResource.set(
              'totalAllocation',
              getAllocatedMinutesFromAggregation(childResource.projectId, {
                type: ResourceType.User,
                userId: childResource.userId,
                roleId: childResource.roleId,
                referenceTeamId: childResource.referenceTeam?.teamId,
              }).totalAllocatedMinutes,
            );
          });
        };

        schedulerRef.current.instance.resources.forEach((resource) => {
          if (resource == null) {
            return;
          }
          if (resource.type === SchedulerResourceType.TeamHeader) {
            resource.children.forEach((roleResource) => {
              setTotalAllocationsForRoleResource(roleResource);
            });
          }
          if (resource.type === SchedulerResourceType.User) {
            setTotalAllocationsForUserResource(resource);
          }
          if (resource.type === SchedulerResourceType.PartnerHeader) {
            resource.children.forEach((companyResource) => {
              if (companyResource == null) {
                return;
              }
              companyResource.children.forEach((childResource) => {
                if (childResource == null) {
                  return;
                }
                if (
                  childResource.type === SchedulerResourceType.ExpandableRole
                ) {
                  setTotalAllocationsForRoleResource(childResource);
                } else if (childResource.type === SchedulerResourceType.User) {
                  setTotalAllocationsForUserResource(childResource);
                }
              });
            });
          }
        });

        resolve();
      }),
    [
      getAllocatedMinutesFromAggregation,
      isCollaborator,
      pageParams.viewPreset,
      resourceManagementConfig?.softAllocations.enabled,
      teamMembership,
      userLookup,
      workingDaysConfig,
    ],
  );

  const getSchedulerProjectInfo = useCallback(() => {
    const getResources = () => {
      if (isCollaborator) {
        const usersData = Object.values(usersWithProjects).filter(
          (entity) => entity.user.userId === currentUser.user.userId,
        );

        return convertUsersToResourceTree(usersData, {
          includeAssignProject: !false,
        });
      }

      const usersData = Object.values(usersWithProjects).filter(
        (entity) => entity.user.userType === UserType.Native,
      );
      const userResources = convertUsersToResourceTree(usersData, {
        includeAssignProject: !isCollaborator,
      });

      const teamsWithProject = getTeamsWithProject(
        teamMembership,
        projectLookup,
        teamLookup,
      );

      const teamsWithProjectGroupedByRoleLookup =
        getTeamsWithProjectGroupedByRole(
          filterUnresolvedVendorResponsibleTeams(
            teamsWithProject,
            currentUser.company.companyId,
          ),
          roles,
        );
      const companies = Object.values(partnerCompanyLookup);
      const companyResources =
        companies.length > 0 && projects.length > 0
          ? [
              convertUserGroupedByCompanyToResourceTree({
                companies,
                userIdsGroupedByCompany,
                usersWithProjects,
                teamIdsGroupedByCompany,
                projectLookup,
                roles,
                roleLookup,
                userResourceOptions: {
                  includeAssignProject: !isCollaborator,
                },
              }),
            ]
          : [];
      const userAndCompanyResources = concat(companyResources, userResources);

      return Object.keys(teamsWithProjectGroupedByRoleLookup).length > 0
        ? [
            teamsWithProjectGroupedByRoleAsResource(
              teamsWithProjectGroupedByRoleLookup,
              roleLookup,
            ),
            ...userAndCompanyResources,
          ]
        : userAndCompanyResources;
    };

    return {
      resourceStore: {
        modelClass: GroupByUserResourceModel,
        data: getResources(),
        useRawData: {
          disableDuplicateIdCheck: true,
          disableDefaultValue: true,
          disableTypeConversion: true,
        },
        verifyNoGeneratedIds: false,
        reapplySortersOnAdd: true,
      },
      eventStore: {
        modelClass: AllocationEventModel,
        useRawData: {
          disableDuplicateIdCheck: true,
          disableDefaultValue: true,
          disableTypeConversion: true,
        },
        verifyNoGeneratedIds: false,
      },
      calendarManagerStore: {
        useRawData: true,
        data: getCalendars(workingDaysConfig),
      },
      delayCalculation: false,
      calendar: 'nonWorkingDays',
    };
  }, [
    workingDaysConfig,
    isCollaborator,
    usersWithProjects,
    teamMembership,
    projectLookup,
    teamLookup,
    currentUser.company.companyId,
    currentUser.user.userId,
    roles,
    partnerCompanyLookup,
    projects.length,
    userIdsGroupedByCompany,
    teamIdsGroupedByCompany,
    roleLookup,
  ]);

  const commonManagerProps: CommonManagerProps = useMemo(
    () => ({
      groupBy: GroupBy.Users,
      users,
      teams,
      projects,
      roleLookup,
      teamLookup,
      userLookup,
      projectLookup,
      workingDaysConfig,
      teamMembership,
      schedulerRef,
      excludeNonWorkingDays: true,
      currentViewPreset: pageParams.viewPreset,
      resourceManagementConfig,
    }),
    [
      users,
      teams,
      projects,
      roleLookup,
      teamLookup,
      userLookup,
      projectLookup,
      workingDaysConfig,
      teamMembership,
      pageParams.viewPreset,
      resourceManagementConfig,
    ],
  );

  const { bulkUpdate, loadAllocations, sentRequestIds } =
    useAllocationStateManager(commonManagerProps);

  const {
    allocationModalParams,
    onEventActionCancel,
    onEventActionConfirm,
    onEventDelete,
    onEnableSplitMode,
  } = useAllocationManager({
    ...commonManagerProps,
    resourceManagementConfig,
    bulkUpdate,
  });

  useAllocationWSManager({
    sentRequestIds,
    ...commonManagerProps,
  });

  const {
    removeUserFromProjectInUI,
    assignUserToProject,
    removeTeamFromUI,
    removeAllocationsForResource,
  } = useResourceManager(commonManagerProps);

  const { isResourceStoreLoaded } = useResourcesLoader({
    schedulerRef,
    workingDaysConfig,
    projectInfoProvider: getSchedulerProjectInfo,
  });

  const [projectIdsForSocket, setProjectIdsForSocket] = useState<number[]>([]);

  const onVisibleResourceChange = useCallback(
    (visibleResources: ResourcesToBeFetched<ListOfResourcesToBeFetched>) => {
      setProjectIdsForSocket(
        uniq(
          projectIdsForSocket.concat(
            visibleResources[FetchableResourceType.ProjectList],
          ),
        ),
      );
    },
    [projectIdsForSocket],
  );

  const { isSchedulerLoadedWithData, rangesLoadedInUI } = useAllocationLoader({
    groupBy: GroupBy.Users,
    schedulerRef,
    loadAllocations,
    currentViewPreset: pageParams.viewPreset,
    workingDaysConfig,
    onAllocationLoadHandler: onAllocationLoaded,
    onVisibleResourceChange,
  });

  useEffect(() => {
    if (isSchedulerLoadedWithData && !canInitializeWS) {
      setInitializeWS(isSchedulerLoadedWithData);
    }
  }, [canInitializeWS, isSchedulerLoadedWithData]);

  const rangeLoadedInUI = useMemo(
    () =>
      getCorrectedDateRangeBasedOnView(
        {
          fromDate: getMinimumStartDateFromListOfRanges(rangesLoadedInUI),
          toDate: getMaximumEndDateFromListOfRanges(rangesLoadedInUI),
        },
        pageParams.viewPreset,
        workingDaysConfig,
      ),
    [pageParams.viewPreset, rangesLoadedInUI, workingDaysConfig],
  );

  const commonPropsWithRange: ManagerPropsWithLoadedRange = useMemo(
    () => ({
      ...commonManagerProps,
      rangeLoadedInUI,
    }),
    [commonManagerProps, rangeLoadedInUI],
  );

  const {
    deleteAllocationsForResource,
    convertAllocationsForResource,
    deleteAllocationHandler,
    convertAllocationHandler,
  } = useAllocationsMoreActionsManager(
    commonPropsWithRange,
    removeAllocationsForResource,
  );

  const onTeamUpdate = useCallback(
    (team: Team) => {
      const { teamId, projectId, teamName } = team;

      if (projectId) {
        schedulerRef.current.instance.resourceStore
          .getById(resourceIdForTeamAsLeaf(projectId, teamId))
          .set('teamName', teamName);
      }
    },
    [schedulerRef],
  );

  const { teamIdToBeUpdated, closeEditTeamModal, onEditTeam, updateTeamName } =
    useTeamCellActions({
      teamLookup,
      onTeamUpdate,
    });

  const { teamDataForFindMember, closeFindMemberModal, onFindMember } =
    useFindMember();

  const columnConfig = useColumnsConfigForGroupByUser({
    schedulerRef,
    userLookup,
    projectLookup,
    projectStatusLookup,
    softAllocationsEnabled:
      resourceManagementConfig?.softAllocations.enabled ?? false,
    onFindMember,
    onEditTeam,
    deleteAllocationHandler,
    convertAllocationHandler,
  });

  const {
    availableResources,
    currentAvailabilityFilter,
    availabilityFilterChangeHandler,
  } = useAvailabilityFilter({
    schedulerRef,
    isSchedulerLoadedWithData,
    pageParams,
    setPageParams,
    isHoursPerWeek,
  });

  const { exportModalVisibility, hideExportModal, showExportModal, onExport } =
    useControlExport({
      groupBy: GroupBy.Users,
      viewPreset: pageParams.viewPreset,
      filter: pageParams.filter,
      defaultGroup: FilterGroupType.User,
    });

  useEventsTooltip(schedulerRef, userLookup);
  useUserAssignedToProject(schedulerRef, assignUserToProject);
  useDeleteAllocations(schedulerRef, deleteAllocationsForResource);
  useConvertAllocations(schedulerRef, convertAllocationsForResource);

  useToStartOfProject(
    schedulerRef,
    useCallback(
      ({ projectId }) => {
        const project = projects.find((x) => x.projectId === projectId);

        if (project == null) {
          return;
        }
        schedulerRef.current.instance.scrollToDate(
          moment(project.startDate).toDate(),
          {
            block: 'start',
          },
        );
      },
      [projects],
    ),
  );

  useUserRemovedFromProject(
    schedulerRef,
    useCallback(
      (params: UserRemovedFromProjectParams) =>
        removeUserFromProjectInUI({ ...params, rangeLoadedInUI }),
      [rangeLoadedInUI, removeUserFromProjectInUI],
    ),
  );

  useTeamRemovedFromProject(
    schedulerRef,
    useCallback(
      ({ projectId, teamId }) => {
        if (teamLookup[teamId] != null) {
          return removeTeamFromUI(
            projectId,
            teamLookup[teamId],
            rangeLoadedInUI,
          );
        }

        return undefined;
      },
      [rangeLoadedInUI, removeTeamFromUI, teamLookup],
    ),
  );

  useViewPresetManager({
    loading: !isStateRestored,
    schedulerRef,
    currentPresetId: pageParams.viewPreset,
    onPresetChange: useCallback(
      (viewPreset, clearEvents) => {
        if (
          clearEvents ||
          getUserSelectablePresetId(viewPreset) !==
            getUserSelectablePresetId(pageParams.viewPreset)
        ) {
          schedulerRef.current.instance.suspendRefresh();
          schedulerRef.current.instance.eventStore.removeAll();
          schedulerRef.current.instance.resumeRefresh(true);
          queueMacrotask(() => {
            schedulerRef.current.instance.trigger('onViewPresetChange');
          });
        }

        setPageParams({
          ...pageParams,
          viewPreset,
        });
      },
      [pageParams, setPageParams],
    ),
  });

  usePeopleViewFilter({
    hideUnavailableAreas,
    canApplyFilter: isResourceStoreLoaded,
    filter: pageParams.filter,
    schedulerRef,
    searchText,
    users,
    currentViewPreset: pageParams.viewPreset,
    availableResources,
    availabilityFilter: currentAvailabilityFilter,
    onFilterReset,
  });
  const [showSourceTasksForAllocation, setAllocationForShowingSourceTasks] =
    useState<Allocation>();
  const sourceTasksGetter = useCallback(
    (allocation) =>
      ResourceManagmentApi.getSourceTasksForAllocationId(allocation.id),
    [],
  );

  useShowSourceTasks(schedulerRef, ({ event }) =>
    setAllocationForShowingSourceTasks(asAllocationPayload(event)),
  );

  const actions = useMemo(
    () => ({
      primary: isCollaborator ? null : (
        <>
          <ViewsComponent
            groupBy={GroupBy.Users}
            isFieldsLoading={isUserFieldsLoading || isProjectFieldsLoading}
            lookup={groupedLookupsByName}
            currentDefinition={{
              filter: pageParams.filter,
            }}
            currentViewId={pageParams.viewId ?? DEFAULT_VIEW_ID}
            fields={groupedFields}
            defaultViews={defaultViews}
            onViewSelect={({ viewId, viewDefinition }) =>
              setPageParams({ ...pageParams, ...viewDefinition, viewId })
            }
            onFilterChange={(filter) =>
              setPageParams({ ...pageParams, filter })
            }
          />
          <AvailabilityFilter
            userFields={userFields}
            availabilityFilter={currentAvailabilityFilter}
            appliedFilter={{
              ...pageParams.filter,
              groupCriteria: pageParams.filter.groupCriteria.filter(
                ({ id }) => id === FilterGroupType.User,
              ),
            }}
            onFilterChange={availabilityFilterChangeHandler}
            isHoursPerWeek={isHoursPerWeek}
          />
          {currentAvailabilityFilter != null ? (
            <SwitchWrapper>
              <Switch
                checked={hideUnavailableAreas}
                size="small"
                onChange={setHideUnavailableAreas}
              />
              <FormattedMessage id="resource_management.availability_criteria.show_only_available_area" />
            </SwitchWrapper>
          ) : null}
        </>
      ),
      secondary: (
        <SecondaryToolbar
          zoomPresets={zoomPresets}
          viewPresetIds={
            isHoursPerWeek
              ? UserSelectablePresetIdsForHoursPerWeek
              : UserSelectablePresetIds
          }
          currentPreset={pageParams.viewPreset}
          searchText={searchText}
          onViewPresetChange={(presetId) => {
            schedulerRef.current.instance.viewPreset = presetId;
          }}
          zoomIn={() => {
            schedulerRef.current.instance.zoomIn();
          }}
          zoomOut={() => {
            schedulerRef.current.instance.zoomOut();
          }}
          shiftNext={() => {
            schedulerRef.current.instance.shiftNext();
          }}
          shiftPrevious={() => {
            schedulerRef.current.instance.shiftPrevious();
          }}
          goToToday={() => {
            schedulerRef.current.instance.scrollToNow({
              block: 'center',
            });
          }}
          onSearchChange={(text) => setSearchText(text)}
          onExport={isCollaborator ? undefined : showExportModal}
        />
      ),
    }),
    [
      isCollaborator,
      isUserFieldsLoading,
      isProjectFieldsLoading,
      groupedLookupsByName,
      pageParams,
      isHoursPerWeek,
      groupedFields,
      defaultViews,
      userFields,
      currentAvailabilityFilter,
      availabilityFilterChangeHandler,
      hideUnavailableAreas,
      zoomPresets,
      searchText,
      showExportModal,
      setPageParams,
    ],
  );

  return (
    <>
      <Helmet>
        <title>{browserTitle}</title>
      </Helmet>
      <ResourceManagementPage
        currentGroupBy={GroupBy.Users}
        currentViewPreset={pageParams.viewPreset}
        actions={actions}
        content={
          <>
            <SchedulerContainer>
              <Scheduler
                ref={schedulerRef}
                columns={columnConfig}
                newEventAllowedOn={useMemo(
                  () => [
                    SchedulerResourceType.Project,
                    SchedulerResourceType.Team,
                  ],
                  [],
                )}
                zoomPresets={zoomPresets}
                workingDaysConfig={workingDaysConfig}
                resourceManagementConfig={resourceManagementConfig}
              />
              {isSchedulerLoadedWithData ? <Legends /> : null}
            </SchedulerContainer>
            {allocationModalParams != null ? (
              <ManageAllocationDrawer
                userLookup={userLookup}
                projectLookup={projectLookup}
                teamLookup={teamLookup}
                allocationDetail={allocationModalParams}
                softAllocationsEnabled={
                  resourceManagementConfig?.softAllocations.enabled ?? false
                }
                onClose={onEventActionCancel}
                onDone={onEventActionConfirm}
                onDelete={onEventDelete}
                onEnableSplitMode={onEnableSplitMode}
                workingDaysConfig={workingDaysConfig}
                isHoursPerWeek={isHoursPerWeek}
              />
            ) : null}

            {teamDataForFindMember != null ? (
              <FindMemberModal
                teamData={teamDataForFindMember}
                onClose={closeFindMemberModal}
                projectLookup={projectLookup}
                roleLookup={roleLookup}
              />
            ) : null}
            {teamIdToBeUpdated ? (
              <PlaceholderCreateOrEditModal
                team={teamLookup[teamIdToBeUpdated]}
                roles={roles}
                placeholders={teams}
                primaryButtonLabel={<FormattedMessage id="save" />}
                save={updateTeamName}
                cancel={closeEditTeamModal}
              />
            ) : null}
            <ExportAsCSVModal
              visible={exportModalVisibility}
              onCancel={hideExportModal}
              onExportFinished={hideExportModal}
              exportHandler={onExport}
            />
            {canInitializeWS ? (
              <ResourceManagementSocketConnector
                {...commonPropsWithRange}
                projectIds={projectIdsForSocket}
              />
            ) : null}
            <AutoAllocateSourceTasksDrawer
              schedulerRef={schedulerRef}
              userLookup={userLookup}
              teamLookup={teamLookup}
              projectLookup={projectLookup}
              allocation={showSourceTasksForAllocation}
              sourceTasksGetter={sourceTasksGetter}
              onCancel={() => setAllocationForShowingSourceTasks(undefined)}
              isHoursPerWeek={isHoursPerWeek}
              workingDaysConfig={workingDaysConfig}
            />
          </>
        }
      />
    </>
  );
};
